import { useCallback, useMemo, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Link, useRouter } from '@tanstack/react-router';
import { FolderKanban, Plus } from 'lucide-react';
import { DataTable } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { SearchInput } from '@/components/common/SearchInput';
import { buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { formatNumber } from '@/utils/number';
import { pcmpProjectListInfiniteQueryOptions } from '../api/pcmpProjects.api';
import { usePcmpProjectFilters } from '../hooks/usePcmpProjectFilters';
import { useSetPcmpProjectStatus } from '../hooks/useSetPcmpProjectStatus';
import type { PcmpProjectListItem } from '../types/pcmpProject.types';
import { getPcmpProjectColumns } from './PcmpProjectColumns';
import {
  PcmpProjectDeleteConfirmation,
  type PcmpProjectDeleteTarget,
} from './PcmpProjectDeleteConfirmation';
import { PcmpProjectFilterBar } from './PcmpProjectFilterBar';

export function PcmpProjectListTable() {
  const router = useRouter();
  const controller = usePcmpProjectFilters();
  const { hasActiveFilters, listQueryParams, appliedFilters } = controller;
  const [deleteTarget, setDeleteTarget] = useState<PcmpProjectDeleteTarget | null>(null);
  const { mutate: setStatus } = useSetPcmpProjectStatus();

  const {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
  } = useInfiniteQuery(pcmpProjectListInfiniteQueryOptions(listQueryParams));

  const projects = useMemo(() => data?.pages.flatMap((page) => page.data) ?? [], [data]);
  const totalCount = data?.pages[0]?.meta.total;

  const openProject = useCallback(
    (project: PcmpProjectListItem) => {
      void router.navigate({
        to: '/pcmp/projects/$projectId',
        params: { projectId: project.id },
      });
    },
    [router],
  );

  const columns = useMemo(
    () =>
      getPcmpProjectColumns(appliedFilters.search || undefined, {
        onView: openProject,
        onEdit: (project: PcmpProjectListItem) =>
          void router.navigate({
            to: '/pcmp/projects/$projectId/edit',
            params: { projectId: project.id },
          }),
        onToggleActive: (project: PcmpProjectListItem) =>
          setStatus({
            id: project.id,
            displayName: project.displayName,
            isActive: !project.isActive,
          }),
        onDelete: (project: PcmpProjectListItem) => setDeleteTarget(project),
      }),
    [appliedFilters.search, setStatus, openProject, router],
  );

  return (
    <div className="flex flex-1 flex-col gap-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-semibold">PCMP Projects</h1>
          {hasActiveFilters && !isLoading && !error && totalCount !== undefined ? (
            <span className="text-sm text-muted-foreground tabular-nums" aria-live="polite">
              {formatNumber(projects.length)} of {formatNumber(totalCount)}
            </span>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-3 sm:justify-end">
          <SearchInput
            value={controller.filters.search}
            onChange={controller.handleSearchChange}
            placeholder="Search by no, name or code"
            className="flex-1 min-w-48 lg:max-w-xs"
          />
          <Link
            to="/pcmp/projects/new"
            className={cn(buttonVariants({ variant: 'default' }), 'gap-1.5')}
          >
            <Plus className="size-4" />
            Create Project
          </Link>
        </div>
      </div>

      <PcmpProjectFilterBar controller={controller} />

      {error ? (
        <ErrorState
          error={error}
          fallbackTitle="Could not load projects"
          onRetry={() => void refetch()}
          className="min-h-0 flex-1"
        />
      ) : (
        <DataTable
          data={projects}
          columns={columns}
          isLoading={isLoading}
          stickyFirstColumn
          fillHeight
          emptyIcon={FolderKanban}
          onLoadMore={() => void fetchNextPage()}
          hasMore={hasNextPage}
          isLoadingMore={isFetchingNextPage}
          loadMoreError={isFetchNextPageError}
          onRowClick={openProject}
          className="flex-1 min-h-0"
        />
      )}

      <PcmpProjectDeleteConfirmation
        project={deleteTarget}
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      />
    </div>
  );
}
